import useBoolean from '@eduzz/houston-hooks/useBoolean';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Field, Form, Formik } from 'formik';
import React, { memo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import MyModal from 'v2/components/Shared/MyModal';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML/index';
import { logError } from 'v2/helpers/rxjs-operators/logError';
import { profileFactory } from 'v2/services/profile';
import { termsFactory } from 'v2/services/terms';

import type IProfile from 'v2/interfaces/profile';
import type { IModalFiscalForm } from 'v2/interfaces/terms';

import Address from './address';
import ModalFiscaSchema from './validation';
import { CheckboxFormField } from '../FormFields/CheckboxFormField';
import { TextFormField } from '../FormFields/TextFormField';
import ModalMinor from '../ModalMinor';

import type { FormikProps } from 'formik';

interface IProps {
  profile: IProfile;
  opened: boolean;
  onClose: () => void;
  onComplete: (profile?: IProfile) => void;
  subtitle?: string;
  returnDataOnComplete?: boolean;
}

const useStyles = makeStyles(() => ({
  marginLeft: {
    marginLeft: 4,
  },
  termContainer: {
    borderRadius: 4,
    backgroundColor: '#f3f3f3',
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: 8,
    width: '100%',
    marginTop: '0.3rem',
  },
}));

const ModalFiscal = memo(({ opened, onClose, onComplete, profile, subtitle, returnDataOnComplete }: IProps) => {
  const classes = useStyles({});
  const [loading, setLoading] = useState(false);
  const [modalMinor, , openModalMinor, closeModalMinor] = useBoolean();
  const { search } = useLocation();

  const [term] = useObservable(() => termsFactory.loadFiscalTerm().pipe(logError()), []);

  const [updateUserAction] = useCallbackObservable((model: IModalFiscalForm) => {
    setLoading(true);
    return profileFactory.updateProfile(model).pipe(
      tap((res) => {
        toast.success('Perfil alterado com sucesso.');
        setLoading(false);
        const searchParams = new URLSearchParams(search);

        if (returnDataOnComplete && res && res.data && res.data.profile) {
          onComplete(res.data.profile);
          if (searchParams.has('backTo')) {
            window.location.replace(searchParams.get('backTo'));
          }
          return;
        }

        onComplete();
        if (searchParams.has('backTo')) {
          window.location.replace(searchParams.get('backTo'));
        }
      }),
      catchError((err) => {
        if (err == 'DOC_UNDER_AGE') {
          openModalMinor();
          return of(true);
        }
        if (err == 'ON_BLACKLIST') {
          setLoading(false);
          toast.error('Não é possível alterar o perfil. Entre em contato com o suporte.');
          return of(true);
        }
        setLoading(false);
        toast.error('Erro ao atualizar perfil. Tente novamente!');
        return of(true);
      })
    );
  }, []);

  if (!opened) {
    return null;
  }

  return (
    <>
      <ModalMinor opened={modalMinor} onClose={closeModalMinor} />
      <Formik
        initialValues={{
          address_zip_code: profile?.address_zip_code || '',
          address_street: profile?.address_street || '',
          address_neighborhood: profile?.address_neighborhood || '',
          address_state: profile?.address_state || '',
          address_number: profile?.address_number?.toString() || '',
          address_complement: profile?.address_complement || '',
          address_city_name: profile?.address_city_name || '',
          document_number: profile?.document_number || '',
          type: profile?.type || '',
          address_country: 'Brasil',
          fiscal_responsibility: false,
          producer_affiliate_terms: !!profile?.producer_affiliate_terms,
        }}
        onSubmit={updateUserAction}
        validationSchema={ModalFiscaSchema}
      >
        {(formikProps: FormikProps<IModalFiscalForm>) => {
          const getError = (field: keyof IModalFiscalForm): any => {
            if (formikProps.touched[field] && formikProps.errors[field]) return formikProps.errors[field];
            return null;
          };

          return (
            <MyModal
              opened={opened}
              submitLoading={loading}
              onSubmit={formikProps.submitForm}
              onClose={onClose}
              title='Complete seu perfil'
              message={subtitle ?? 'Para continuar com sua transferência, preencha os campos abaixo:'}
            >
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      label='CPF/CNPJ'
                      name='document_number'
                      id='document_number'
                      mask='document'
                      disabled={Boolean(formikProps.initialValues.document_number)}
                      component={TextFormField}
                    />
                  </Grid>

                  <Address {...formikProps} getError={getError} />

                  <Grid item xs={12}>
                    <div className={classes.marginLeft}>
                      <Field
                        label={<p>Aceito o termo de responsabilidade fiscal</p>}
                        name='fiscal_responsibility'
                        id='fiscal_responsibility'
                        component={CheckboxFormField}
                      />
                    </div>
                    <NoDangerouslyHTML className={classes.termContainer} html={term?.hint ?? ''} />
                  </Grid>

                  {!!!profile?.producer_affiliate_terms && (
                    <Grid item xs={12}>
                      <div className={classes.marginLeft}>
                        <Field
                          label={
                            <>
                              Aceito os{' '}
                              <a href='https://eduzz.com/termos/' target='_blank' rel='noopener noreferrer'>
                                termos de uso
                              </a>
                            </>
                          }
                          name='producer_affiliate_terms'
                          id='producer_affiliate_terms'
                          component={CheckboxFormField}
                        />
                      </div>
                    </Grid>
                  )}
                </Grid>
              </Form>
            </MyModal>
          );
        }}
      </Formik>
    </>
  );
});

export default ModalFiscal;
